import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  Demo,
  DemoDetail,
  DemosFilters,
  CreateDemoPayload,
  UpdateDemoPayload,
  FeedbackPayload,
} from '../types';

// ─── Queries ────────────────────────────────────────────────────────────────

export function useDemos(filters: DemosFilters = {}) {
  return useQuery({
    queryKey: ['demos', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.mine) params.set('mine', '1');
      const qs = params.toString();
      const { data } = await api.get<{ demos: Demo[] }>(`/api/demos${qs ? `?${qs}` : ''}`);
      return data.demos ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useDemo(id: string | null) {
  return useQuery({
    queryKey: ['demos', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get<DemoDetail>(`/api/demos/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useDemoMutations() {
  const queryClient = useQueryClient();
  const invalidate = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: ['demos'] });
    if (id) queryClient.invalidateQueries({ queryKey: ['demos', 'detail', id] });
  };

  const createDemo = useMutation({
    mutationFn: async (payload: CreateDemoPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string; demo_code: string }>('/api/demos', payload);
      return data;
    },
    onSuccess: () => invalidate(),
  });

  const updateDemo = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateDemoPayload }) => {
      const { data } = await api.put<{ ok: boolean }>(`/api/demos/${id}`, payload);
      return data;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });

  const assignDemo = useMutation({
    mutationFn: async ({ id, assigned_to }: { id: string; assigned_to: string | null }) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/demos/${id}/assign`, { assigned_to });
      return data;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });

  const addComment = useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment: string }) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/demos/${id}/comments`, { comment });
      return data;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });

  const addFeedback = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FeedbackPayload }) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/demos/${id}/feedback`, payload);
      return data;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });

  const deleteDemo = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/demos/${id}`);
      return data;
    },
    onSuccess: () => invalidate(),
  });

  return { createDemo, updateDemo, assignDemo, addComment, addFeedback, deleteDemo };
}
